import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, useTheme, IconButton, Paper, alpha, type SxProps } from '@mui/material';
import { format } from 'date-fns';
import { HeatmapCircle } from '@visx/heatmap';
import { AxisLeft } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { depthToColor } from '../../utils/layer_style';
import { getDaysInMonth } from 'date-fns';
import { interpolateCubehelixDefault } from 'd3-scale-chromatic';
import { useDragSelection, ConfirmTimeRangeChip } from './Drag';

export type EventLike = { date: Date; value: number; label?: string };



const AGG_FNS: Record<'max' | 'sum' | 'count', (acc: number, x: number) => number> = {
  max: (acc, x) => (x > acc ? x : acc),
  sum: (acc, x) => acc + x,
  count: (acc, _x) => acc + 1,
}


function VisxCalendarHeatmap({
  events,
  fromTime,
  toTime,
  cell = 11,
  gap = 1,
  onSelectRange,
  selectionFrom,
  selectionTo,
  confirmBeforeApply=true,
  sx,
  valueMode = 'max',
  units,
  tooltipRender,
  children,
}: {
  events: EventLike[];
  fromTime: Date;
  toTime: Date;
  onSelectRange?: (timeRange: [Date, Date]) => void;
  selectionFrom?: Date;
  selectionTo?: Date;
  confirmBeforeApply?: boolean;
  cell?: number;
  gap?: number;
  sx?: SxProps;
  valueMode?: 'max' | 'count' | 'sum';
  units?: string;
  tooltipRender?: (args: { date: Date; events: EventLike[]; value: number; valueMode: 'max'|'count'|'sum'; units?: string }) => string;
  children?: (args: { year: number; events: EventLike[]; from: Date; to: Date; buckets: Map<number, number>; eventsByDay: Map<number, EventLike[]>; valueMode: 'max'|'count'|'sum'; units?: string }) => React.ReactNode;
}) {
  const theme = useTheme();
  const textColorPrimary = theme.palette.text.primary;
  const textColorSecondary = theme.palette.text.secondary;


  const monthHeight = cell + 4;
  const widthPerYear = 32 + 32 * cell;
  const svgHeight = 12 * monthHeight + cell; // small buffer to avoid clipping with reversed band range


  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  
  // Index events by day (midnight) and aggregate per-day values according to valueMode
  const { buckets, eventsByDay, countsMax } = React.useMemo(() => {
    const dayMs = 24 * 60 * 60 * 1000;
    const start = new Date(fromTime);
    start.setHours(0, 0, 0, 0);
    const end = new Date(toTime);
    end.setHours(0, 0, 0, 0);
    const buckets = new Map<number, number>();
    const eventsByDay = new Map<number, EventLike[]>();
    for (let t = +start; t <= +end; t += dayMs) { buckets.set(t, 0); eventsByDay.set(t, []); }
    for (const e of events || []) {
      const d = new Date(e.date);
      d.setHours(0, 0, 0, 0);
      const key = +d;
      if (!buckets.has(key)) continue;
      const list = eventsByDay.get(key)!;
      list.push(e);
    }
    // aggregate using AGG_FNS
    let countsMax = 0;
    for (const [key, list] of eventsByDay.entries()) {
      if (valueMode === 'count') {
        buckets.set(key, list.length);
        if (list.length > countsMax) countsMax = list.length;
      } else {
        const agg = list.reduce((acc, e) => AGG_FNS[valueMode](acc, e.value), 0);
        buckets.set(key, agg);
      }
    }
    return { buckets, eventsByDay, countsMax };
  }, [events, fromTime, toTime, valueMode]);


  
  const yearsData = React.useMemo(() => {
    const startYear = fromTime.getFullYear();
    const endYear = toTime.getFullYear();

    const buildYearDataDayOuter = (year: number) => {
      // Days 0..30 (index) as outer bins; inner bins are months 0..11
      // We use 0-based indices to align with HeatmapCircle's row accessors reliably
      const days = Array.from({ length: 31 }, (_, i) => i);
      const months = Array.from({ length: 12 }, (_, i) => i);
      const data: { bin: number; bins: { bin: number; count?: number }[] }[] = days.map((day) => ({
        bin: day,
        bins: months.map((month) => {
          // Respect deployment range on edges
          const beforeStart = year === fromTime.getFullYear() && month < fromTime.getMonth();
          const afterEnd = year === toTime.getFullYear() && month > toTime.getMonth();
          const dim = getDaysInMonth(new Date(year, month, 1));
          if (beforeStart || afterEnd || (day + 1) > dim) return { bin: month, count: undefined };

          const dt = new Date(year, month, day + 1);
          dt.setHours(0, 0, 0, 0);
          if (+dt < +fromTime || +dt > +toTime) return { bin: month, count: undefined };
          const v = buckets.get(+dt) ?? 0;
          return { bin: month, count: v };
        }),
      }));
      return data;
    };

    return Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i).map((year) => {
      const yearData = buildYearDataDayOuter(year);
      const xdomain = yearData.map(d => d.bin);
      // const ydomain = Array.from({ length: 12 }, (_, i) => i);
      const ydomain = yearData.reduce<number[]>((acc, d) => {
        d.bins.forEach(b => {
          if (!acc.includes(b.bin) && b.count !== undefined) acc.push(b.bin);
        });
        return acc;
      }, []).sort((a, b) => a - b);

      const xScale = scaleBand<number>({
        domain: xdomain,
        range: [0, xdomain.length * cell],
        padding: 0.1,
      });

      const yScale = scaleBand<number>({
        domain: ydomain, // months 0..11
        range: [ydomain.length * monthHeight, 0],
        padding: 0.1,
      });
      return [year, yearData, xScale, yScale] as const;
    });
  }, [buckets, fromTime, toTime, cell, monthHeight]);

  // const maxV = React.useMemo(() => {
  //   let m = 0;
  //   for (const v of buckets.values()) m = Math.max(m, v);
  //   return m;
  // }, [buckets]);
  const colorFor = React.useCallback((v: number) => {
    if (v <= 0) return theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
    if (valueMode === 'count') {
      // const n = countsMax > 0 ? Math.min(1, v / countsMax) : 0.1;
      // const alphaV = 0.18 + 0.62 * n;
      // const blue = theme.palette.mode === 'dark' ? [80, 160, 255] : [0, 132, 255];
      // return `rgba(${blue[0]}, ${blue[1]}, ${blue[2]}, ${alphaV.toFixed(3)})`;
      return interpolateCubehelixDefault(Math.min(1, Math.log2(v + 1) / Math.log2(countsMax + 1)));
    }
    return `rgba(${depthToColor(v)})`;
  }, [theme.palette.mode, valueMode]);

  const {
    isDragging, dragStart, dragEnd, updateDrag, commitDrag, confirmOpen
  } = useDragSelection({ onSelectRange, confirmBeforeApply });

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, position: 'relative', ...sx }}>
      {yearsData.map(([year, yearData, xScale, yScale]) => {
        const selFromYear = selectionFrom ? selectionFrom.getFullYear() : null;
        const selToYear = selectionTo ? selectionTo.getFullYear() : null;
        const dragStartYear = dragStart ? dragStart.getFullYear() : null;
        const dragEndYear = dragEnd ? dragEnd.getFullYear() : null;
        const isYearSelected = (selFromYear === year) || (selToYear === year) || (selFromYear! < year && selToYear! > year);
        const isYearDragging = (dragStartYear === year) || (dragEndYear === year) || (dragStartYear! < year && dragEndYear! > year);

        const block = (
          <YearBlock
            key={year}
            year={year}
            yearData={yearData}
            xScale={xScale}
            yScale={yScale}
            cell={cell}
            gap={gap}
            widthPerYear={widthPerYear}
            textColorSecondary={textColorSecondary}
            selectionFrom={isYearSelected ? selectionFrom ?? null : null}
            selectionTo={isYearSelected ? selectionTo ?? null : null}
            fromTime={fromTime}
            toTime={toTime}
            colorFor={colorFor}
            dragStart={isYearDragging ? dragStart ?? null : null}
            dragEnd={isYearDragging ? dragEnd ?? null : null}
            updateDrag={updateDrag}
            commitDrag={commitDrag}
            tooltipRef={tooltipRef}
            eventsByDay={eventsByDay}
            valueMode={valueMode}
            tooltipRender={tooltipRender}
            units={units}
          />
        );

        return (
          children ?
          <Box key={year} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {block}
            <Box sx={{ minWidth: 200 }}>
              {children({ year, events: yearData, eventsByDay, valueMode, units })}
            </Box>
          </Box>
          : block
        );
      })}

      {createPortal(
        <Box
          ref={tooltipRef}
          sx={{
            position: 'fixed',
            display: 'block',
            pointerEvents: 'none',
            background: theme.palette.background.opaque,
            color: theme.palette.text.primary,
            transition: 'opacity 0.5s ease-in-out',
            opacity: 0,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            boxShadow: theme.shadows[3],
            fontSize: '0.75rem',
            zIndex: 3000,
          }}
        />,
        document.body
      )}

      {/* Confirm chip (inline, above charts) */}
      {confirmBeforeApply && confirmOpen && dragStart && dragEnd && (
        <Box sx={{ position: 'absolute', top: -10, left: 32, right: 8, display: 'flex', justifyContent: 'flex-end', pointerEvents: 'auto' }}>
          <ConfirmTimeRangeChip from={dragStart} to={dragEnd} onConfirm={() => { commitDrag(true); }} onCancel={() => { commitDrag(false); }} />
        </Box>
      )}
    </Box>
  );
}




const SelectionBands = ({ from, to, year, xScale, yScale, fill }: { from: Date | null; to: Date | null; year: number; xScale: any; yScale: any; fill?: string }) => {
  // Draw selection rectangles per month row, only where the range intersects
  return <>{from && to ? Array.from({ length: 12 }, (_, i) => i).map(m => {
    const monthStart = new Date(year, m, 1);
    const monthEnd = new Date(year, m, getDaysInMonth(monthStart), 23, 59, 59, 999);
    // intersect selection with both the visible calendar range and this month
    const selStart = new Date(Math.max(+monthStart, +from));
    const selEnd = new Date(Math.min(+monthEnd, +to));
    if (+selEnd < +selStart) return null;

    const dayStart = Math.max(1, selStart.getDate());
    const dayEnd = Math.min(getDaysInMonth(monthStart), selEnd.getDate());
    const x1 = (xScale(dayStart - 1) ?? 0);
    const x2 = (xScale(dayEnd - 1) ?? 0) + xScale.bandwidth();
    const x = Math.min(x1, x2) - 1;
    const w = Math.max(0, Math.abs(x2 - x1)) + 5;
    const y = yScale(m) ?? 0;
    const h = yScale.bandwidth();
    return (
      <rect
        key={`sel-${year}-${m}`}
        x={x}
        y={y}
        width={w}
        height={h}
        rx={8}
        ry={9}
        fill={fill ? alpha(fill, 0.12) : "rgba(14, 144, 137, 0.12)"}
        stroke={fill ? alpha(fill, 0.5) : "rgba(14, 144, 137, 0.5)"}
        strokeWidth={2}
        style={{ pointerEvents: 'none' }}
      />
    )
  }) : null}</>;
}

// Year block: memoized container for one year's grid, with stable handlers
const YearBlock = React.memo(function YearBlock({
  year,
  yearData,
  xScale,
  yScale,
  cell,
  gap,
  widthPerYear,
  textColorSecondary,
  selectionFrom,
  selectionTo,
  fromTime,
  toTime,
  colorFor,
  dragStart,
  dragEnd,
  updateDrag,
  commitDrag,
  tooltipRef,
  eventsByDay,
  valueMode,
  tooltipRender,
  units,
}: {
  year: number;
  yearData: any[];
  xScale: any;
  yScale: any;
  cell: number;
  gap: number;
  widthPerYear: number;
  textColorSecondary: string;
  selectionFrom?: Date;
  selectionTo?: Date;
  fromTime: Date;
  toTime: Date;
  colorFor: (v: number) => string;
  dragStart: Date | null;
  dragEnd: Date | null;
  updateDrag: (d: Date) => void;
  commitDrag: (confirm?: boolean) => void;
  tooltipRef: React.MutableRefObject<HTMLDivElement | null>;
  eventsByDay: Map<number, EventLike[]>;
  valueMode: 'max' | 'count' | 'sum';
  tooltipRender?: (args: { date: Date; events: EventLike[]; value: number; valueMode: 'max'|'count'|'sum'; units?: string }) => string;
  units?: string;
}) {
  const onPointerUp = React.useCallback((e: React.PointerEvent<SVGGElement>) => {
    if (!dragStart) return;
    commitDrag();
    try { (e.currentTarget as any).releasePointerCapture?.((e as any).pointerId); } catch {}
  }, [dragStart, commitDrag]);

  const onPointerCancel = React.useCallback((e: React.PointerEvent<SVGGElement>) => {
    if (!dragStart) return;
    commitDrag(false);
    try { (e.currentTarget as any).releasePointerCapture?.((e as any).pointerId); } catch {}
  }, [dragStart, commitDrag]);

  const xScaleFn = React.useCallback((day: number) => xScale(day), [xScale]);
  const yScaleFn = React.useCallback((month: number) => yScale(month), [yScale]);
  const colorForFn = React.useCallback((depth: number) => colorFor(depth), [colorFor]);
  const opacityScaleFn = React.useCallback((val: number | undefined) => (val === undefined ? 0 : 1), []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', '& circle:hover': { opacity: 1 } }}>
      <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.8 }}>
        {year}
      </Typography>

      <svg width={widthPerYear} height={yScale.range()[0] + cell}>
        <Group top={0} left={32} style={{ touchAction: 'none' }} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}>
          <SelectionBands from={selectionFrom} to={selectionTo} year={year} xScale={xScale} yScale={yScale} fill="rgb(0, 174, 255)" />
          <SelectionBands from={dragStart} to={dragEnd} year={year} xScale={xScale} yScale={yScale} fill="rgb(255, 255, 0)" />
          <CalendarHeatmapAxisLeft year={year} yScale={yScale} fromTime={fromTime} toTime={toTime} textColorSecondary={textColorSecondary} />
          

          <HeatmapCircle
            data={yearData}
            xScale={xScaleFn}
            yScale={yScaleFn}
            colorScale={colorForFn}
            opacityScale={opacityScaleFn}
            radius={cell / 2}
            gap={gap}
          >
            {(heatmap) => (
              <Group>
                {heatmap.flatMap((row) =>
                  row.map((cell) => (
                    <CalendarHeatmapCell
                      key={`heatmap-circle-${cell.row}-${cell.column}`}
                      // cell={cell}
                      {...cell} // THIS IS A WORKAROUND SO THAT CELLS CAN BE PROPERLY MEMOIZED
                      year={year}
                      yearData={yearData}
                      gap={gap}
                      updateDrag={updateDrag}
                      commitDrag={commitDrag}
                      tooltipRef={tooltipRef}
                      eventsByDay={eventsByDay}
                      valueMode={valueMode}
                      tooltipRender={tooltipRender}
                      units={units}
                    />
                  ))
                )}
              </Group>
            )}
          </HeatmapCircle>
        </Group>
      </svg>
    </Box>
  );
});

const CalendarHeatmapAxisLeft = React.memo(function CalendarHeatmapAxisLeft({
  year,
  yScale,
  fromTime,
  toTime,
  textColorSecondary,
}: {
  year: number;
  yScale: any;
  fromTime: Date;
  toTime: Date;
  textColorSecondary: string;
}) {
  return (
    <AxisLeft
      hideAxisLine
      hideTicks
      scale={yScale}
      tickFormat={(m: number) => (
        (
          year === fromTime.getFullYear() && 
          m < fromTime.getMonth()
        ) || (
          year === toTime.getFullYear() && 
          m > toTime.getMonth()
        ) ? '' : new Date(year, m, 1).toLocaleString('default', { month: 'short' })
      )}
      stroke={textColorSecondary}
      tickStroke={textColorSecondary}
      tickLabelProps={{
        fill: textColorSecondary,
        fontSize: 11,
        textAnchor: 'end',
        style: { userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' },
        dy: '0.33em',
      }}
    />
  )
})

// Memoized calendar heatmap cell with stable, useCallback-bound handlers
const CalendarHeatmapCell = React.memo(function CalendarHeatmapCell({
  // cell,
  bin, row, column, datum, cx, cy, r, radius, count, opacity, color, // THIS IS A WORKAROUND SO THAT CELLS CAN BE PROPERLY MEMOIZED
  year,
  yearData,
  gap,
  updateDrag,
  commitDrag,
  tooltipRef,
  eventsByDay,
  valueMode,
  tooltipRender,
  units,
}: {
  cell: any;
  year: number;
  yearData: any[];
  gap: number;
  updateDrag: (date: Date, isStart?: boolean) => void;
  commitDrag: (confirm?: boolean) => void;
  tooltipRef: React.MutableRefObject<HTMLDivElement | null>;
  eventsByDay: Map<number, EventLike[]>;
  valueMode: 'max' | 'count' | 'sum';
  tooltipRender?: (args: { date: Date; events: EventLike[]; value: number; valueMode: 'max'|'count'|'sum'; units?: string }) => string;
  units?: string;
}) {
  // THIS IS A WORKAROUND SO THAT CELLS CAN BE PROPERLY MEMOIZED
  const cell = React.useMemo(() => ({bin, row, column, datum, cx, cy, r, radius, gap, count, opacity, color}), [bin, row, column, datum, cx, cy, r, radius, gap, count, opacity, color]);
  const disabled = cell.opacity === 0;

  const dt = React.useMemo(() => {
    const day = yearData[cell.column].bin;
    const month = yearData[cell.column].bins[cell.row].bin;
    const dt = new Date(year, month, day + 1);
    dt.setHours(0, 0, 0, 0);
    return dt;
  }, [cell, year, yearData]);
  const value = React.useMemo(() => {
    const val = yearData[cell.column].bins[cell.row].count ?? 0;
    return val;
  }, [cell, yearData]);

  const onDown = React.useCallback(() => updateDrag(dt, true), [dt, updateDrag]);
  const onEnter = React.useCallback(() => updateDrag(dt), [dt, updateDrag]);
  const onUp = React.useCallback(() => commitDrag(), [commitDrag]);

  const onMove = React.useCallback((e: React.PointerEvent<SVGCircleElement>) => {
    const tooltipEl = tooltipRef.current;
    if (!tooltipEl) return;
    const dayKey = +dt;
    const list = eventsByDay.get(dayKey) || [];
    const html = (tooltipRender || defaultTooltipRender)({ date: dt, events: list, value, valueMode, units });
    tooltipEl.style.opacity = '1';
    tooltipEl.style.display = 'block';
    tooltipEl.innerHTML = html;
    tooltipEl.style.left = `${e.clientX}px`;
    tooltipEl.style.top = `${e.clientY - 16}px`;
  }, [dt, value, valueMode, units, eventsByDay, tooltipRef, tooltipRender]);

  const onLeave = React.useCallback(() => {
    const el = tooltipRef.current;
    if (el) el.style.opacity = '0';
  }, [tooltipRef]);
  // console.log('render cell', year, cell.row, cell.column, cell.cx, cell.cy);

  return !isNaN(cell.cx) && !isNaN(cell.cy) && !isNaN(cell.r) && (
    <circle
      className="visx-heatmap-circle"
      pointerEvents={disabled ? 'none' : 'bounding-box'}
      cx={cell.cx}
      cy={cell.cy}
      r={cell.r}
      style={{ padding: gap, cursor: 'pointer', opacity: 0.8, transition: 'opacity 0.2s ease-in-out' }}
      fill={cell.color}
      fillOpacity={cell.opacity}
      onPointerDown={disabled ? undefined : onDown}
      onPointerEnter={disabled ? undefined : onEnter}
      onPointerUp={disabled ? undefined : onUp}
      onPointerMove={disabled ? undefined : onMove}
      onPointerLeave={disabled ? undefined : onLeave}
    />
  );
});
const defaultTooltipRender = ({ date, events, value, units, valueMode }: { date: Date; events: EventLike[]; value: number; units?: string; valueMode: 'max' | 'count' | 'sum' }) => {
  const dateStr = format(date, 'PP');
  const aggUnit = valueMode === 'count' ? ' events' : (units ? `${units}` : '');
  const sorted = events.sort((a, b) => b.value - a.value).slice(0, 6);

  const formatValue = (v: number) => (valueMode === 'count' ? String(v) : v.toFixed(1));

  const header = `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:12px">
      <div style="flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${dateStr}</div>
      <div style="flex:0 0 auto;font-weight:600">${formatValue(value)}${aggUnit}</div>
    </div>
  `;

  const items = sorted.map(e => `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:4px">
      <div style="flex:1;opacity:0.95;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${e.label}</div>
      <div style="flex:0 0 auto;opacity:0.9">${(valueMode === 'count' ? String(e.value) : e.value.toFixed(1))}${units ? `${units}` : ''}</div>
    </div>
  `).join('');

  const more = events.length > sorted.length ? `<div style="margin-top:6px;opacity:0.75">and ${events.length - sorted.length} more...</div>` : '';

  return `${header}<div style="margin-top:6px">${items}${more}</div>`;
};

export default React.memo(VisxCalendarHeatmap);